
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { WorkflowFolder } from '@/types/workflow';
import { workflowService } from '@/api/services/workflowService';

interface WorkflowFolderState {
  folders: WorkflowFolder[];
  currentFolderId: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: WorkflowFolderState = {
  folders: [],
  currentFolderId: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchWorkflowFolders = createAsyncThunk(
  'workflowFolder/fetchWorkflowFolders',
  async () => {
    const response = await workflowService.getAllWorkflows();
    return response.workflowFolders;
  }
);

export const createWorkflowFolder = createAsyncThunk(
  'workflowFolder/createWorkflowFolder',
  async (folder: Partial<WorkflowFolder>) => {
    const response = await workflowService.createNewWorkflowFolder(folder);
    return response;
  }
);

export const updateWorkflowFolder = createAsyncThunk(
  'workflowFolder/updateWorkflowFolder',
  async (folder: WorkflowFolder) => {
    // Mock API call - replace with actual API
    return folder;
  }
);

export const deleteWorkflowFolder = createAsyncThunk(
  'workflowFolder/deleteWorkflowFolder',
  async (folderId: string) => {
    const response = await workflowService.deleteWorkflowFolder(folderId);
    return {
      ...response,
      folderId,
    };
  }
);

const workflowFolderSlice = createSlice({
  name: 'workflowFolder',
  initialState,
  reducers: {
    setCurrentWorkflowFolderId: (state, action: PayloadAction<string | null>) => {
      state.currentFolderId = action.payload;
    },
    updateWorkflowFolderIds: (state, action: PayloadAction<{ folderId: string; workflowId: string }>) => {
      const { folderId, workflowId } = action.payload;
      const folder = state.folders.find(f => f.id === folderId);
      if (folder) {
        folder.workflowIds = [...folder.workflowIds, workflowId];
      }
    },
    removeWorkflowFromFolder: (state, action: PayloadAction<{ folderId: string; workflowId: string }>) => {
      const { folderId, workflowId } = action.payload;
      const folder = state.folders.find(f => f.id === folderId);
      console.log('folder', folder);
      if (folder) {
        // Remove the workflow id from the folder
        folder.workflowIds = folder.workflowIds.filter(id => id !== workflowId);
        console.log('folder.workflowIds', folder.workflowIds);
      }
    }
  },
  extraReducers: (builder) => {
    // Fetch folders
    builder
      .addCase(fetchWorkflowFolders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkflowFolders.fulfilled, (state, action) => {
        state.loading = false;
        state.folders = action.payload;
      })
      .addCase(fetchWorkflowFolders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch folders';
      });

    // Create folder
    builder
      .addCase(createWorkflowFolder.fulfilled, (state, action) => {
        state.folders.push(action.payload);
      })
      .addCase(createWorkflowFolder.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to create folder';
      });

    // Update folder
    builder
      .addCase(updateWorkflowFolder.fulfilled, (state, action) => {
        const index = state.folders.findIndex(f => f.id === action.payload.id);
        if (index !== -1) {
          state.folders[index] = action.payload;
        }
      })
      .addCase(updateWorkflowFolder.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update folder';
      });

    // Delete folder
    builder
      .addCase(deleteWorkflowFolder.fulfilled, (state, action) => {
        state.folders = state.folders.filter(f => f.id !== action.payload.folderId || f.parentId !== action.payload.folderId);

        if (state.currentFolderId === action.payload.folderId) {
          state.currentFolderId = null;
        }
      })
      .addCase(deleteWorkflowFolder.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete folder';
      });
  },
});

export const { setCurrentWorkflowFolderId, updateWorkflowFolderIds, removeWorkflowFromFolder } = workflowFolderSlice.actions;
export default workflowFolderSlice.reducer;
